import { useState } from 'react';
import { X, Twitter, AlertCircle, Download, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

import { markWrappedShared, WrappedData } from '@/api/wrapped';

interface WrappedShareModalProps {
  open: boolean;
  onClose: () => void;
  onShareComplete: (pointsAwarded?: number) => void;
  data: WrappedData;
  capturedImage?: string | null;
}

export function WrappedShareModal({ open, onClose, onShareComplete, data, capturedImage }: WrappedShareModalProps) {
  const [step, setStep] = useState<'share' | 'verify'>('share');
  const [tweetUrl, setTweetUrl] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageCopied, setImageCopied] = useState(false);

  if (!open) return null;

  const tweetText = `It's time to show the world what the first Junkies built with @junkfun_ 🌍✨
Open your Junk Fun Wrapped 2025, share your card, and secure your seasonal point bonus.
🔗 junk.fun/wrapped
#JunkFunWrapped #Solana`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;

  const handleOpenTwitter = () => {
    window.open(twitterUrl, '_blank', 'width=550,height=420');
    setStep('verify');
  };

  const handleVerify = async () => {
    if (!tweetUrl.trim()) {
      toast.error('Please paste your tweet URL');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const result = await markWrappedShared(tweetUrl.trim());

      if (result.alreadyShared) {
        toast.info('You already earned your share bonus!');
        handleClose();
        return;
      }

      if (result.success) {
        toast.success(`+${result.pointsAwarded} points earned!`);
        onShareComplete(result.pointsAwarded);
        handleClose();
      } else {
        setError(result.error || 'Verification failed');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      setError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    setStep('share');
    setTweetUrl('');
    setError(null);
    setImageCopied(false);
    onClose();
  };

  const handleCopyImage = async () => {
    if (!capturedImage) return;

    try {
      const blob = await (await fetch(capturedImage)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      setImageCopied(true);
      toast.success('Image copied to clipboard!');
      setTimeout(() => setImageCopied(false), 2000);
    } catch {
      toast.error('Failed to copy image. Try downloading instead.');
    }
  };

  const handleDownloadImage = () => {
    if (!capturedImage) return;

    const link = document.createElement('a');
    link.download = 'junkfun-wrapped-2025.png';
    link.href = capturedImage;
    link.click();
    toast.success('Image downloaded!');
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-gradient-to-b from-[#1a1a2e] to-[#0a0a14] rounded-2xl p-6 max-w-md w-full border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="text-center">
          <h2 className="text-xl font-bold text-white mb-2">
            {step === 'share' ? 'Share Your Wrapped' : 'Verify Your Tweet'}
          </h2>

          {step === 'share' ? (
            <>
              <p className="text-white/60 text-sm mb-4">
                Share your stats on X and earn 500 bonus points!
              </p>

              {/* Captured Image Preview */}
              {capturedImage && (
                <div className="mb-4">
                  <div className="rounded-xl overflow-hidden border border-cyan-500/30 shadow-[0_0_20px_rgba(34,211,238,0.2)]">
                    <img
                      src={capturedImage}
                      alt="Your Wrapped 2025"
                      className="w-full h-auto"
                    />
                  </div>

                  {/* Image action buttons */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleCopyImage}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-colors"
                    >
                      {imageCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {imageCopied ? 'Copied!' : 'Copy Image'}
                    </button>
                    <button
                      onClick={handleDownloadImage}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>

                  <p className="text-white/40 text-xs mt-2">
                    Copy or download your Wrapped card to include in your tweet!
                  </p>
                </div>
              )}

              {/* Tweet preview */}
              <div className="bg-[#0a0a14] rounded-xl p-4 mb-4 border border-white/5 text-left">
                <p className="text-white/80 text-sm whitespace-pre-line leading-relaxed">{tweetText}</p>
              </div>

              {/* Instructions */}
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-4">
                <p className="text-white/70 text-xs">
                  <strong>Required:</strong> Your tweet must mention @junkfun_ to be verified.
                </p>
              </div>

              <button
                onClick={handleOpenTwitter}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-black text-white font-bold rounded-xl hover:bg-zinc-900 border border-white/20 transition-all transform active:scale-95 shadow-[0_4px_15px_rgba(0,0,0,0.4)]"
              >
                <Twitter className="w-5 h-5" />
                Share on X
              </button>
            </>
          ) : (
            <>
              <p className="text-white/60 text-sm mb-6">
                Paste your tweet URL below to verify and earn your bonus points.
              </p>

              {/* Tweet URL input */}
              <div className="mb-4">
                <input
                  type="url"
                  value={tweetUrl}
                  onChange={(e) => setTweetUrl(e.target.value)}
                  placeholder="https://x.com/username/status/..."
                  className="w-full px-4 py-3 bg-[#0a0a14] border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-colors"
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-left">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('share')}
                  className="flex-1 px-6 py-3 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleVerify}
                  disabled={isVerifying || !tweetUrl.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white text-black font-bold rounded-xl hover:bg-white/90 transition-all disabled:opacity-50"
                >
                  {isVerifying ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </>
          )}

          {/* Points badge */}
          {!data.sharePointsAwarded && (
            <div className="mt-4 inline-flex items-center gap-1 px-3 py-1 bg-white/10 text-white rounded-full text-sm">
              +500 points
            </div>
          )}

          {data.sharePointsAwarded && (
            <p className="mt-4 text-white/40 text-sm italic">
              You've already earned your share bonus!
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
